import React from 'react';
import { SelectValue } from 'antd/lib/select';
import { useTranslation } from 'react-i18next';

import { GuardrailErrorPolicy, GuardrailSettingsState } from '../../../../../../gamma-api';
import IbInput from '../../../../../components/common/IbInput';
import IbNumberInput from '../../../../../components/common/IbNumberInput';
import IbSelect from '../../../../../components/common/IbSelect';
import IbSwitch from '../../../../../components/common/IbSwitch';
import IbTypography from '../../../../../components/common/IbTypography';
import { ITEM_CLASS_NAME } from '../../../../../components/common/TypedStepModal/const';
import { GuardrailCheckSettings, getGuardrailErrorPolicyLabel, isSettingsEnabled } from '../../shared/utils';

import CheckBehaviorField from './CheckBehaviorField';

export interface ICommonCheckParametersProps {
  check: GuardrailCheckSettings;
  showErrorPolicy?: boolean;
  showTimeout?: boolean;
  onChange: (patch: Partial<GuardrailCheckSettings>) => void;
  onStateChange?: (state: GuardrailSettingsState) => void;
}

const CommonCheckParameters: React.FC<ICommonCheckParametersProps> = ({
  check,
  showErrorPolicy = true,
  showTimeout = true,
  onChange,
  onStateChange,
}) => {
  const { t } = useTranslation();

  const errorPolicyOptions = [GuardrailErrorPolicy.FailClosed, GuardrailErrorPolicy.FailOpen].map((value) => ({
    value,
    label: getGuardrailErrorPolicyLabel(value),
  }));

  const onEnabledChange = (checked: boolean) => {
    const state = checked ? GuardrailSettingsState.Enabled : GuardrailSettingsState.Disabled;
    if (onStateChange) {
      onStateChange(state);
      return;
    }

    onChange({ state });
  };

  const onBehaviorChange = (behavior: GuardrailCheckSettings['behavior']) => {
    onChange({ behavior });
  };

  const onNameChange = (value: string) => {
    onChange({ name: value });
  };

  const onBlockMessageChange = (value: string) => {
    onChange({ block_message: value });
  };

  const onErrorPolicyChange = (value: SelectValue) => {
    onChange({ on_error: value as GuardrailErrorPolicy });
  };

  const onTimeoutChange = (value: string) => {
    onChange({ timeout_seconds: value ? Number(value) : null });
  };

  const onUseOriginalTextForCheckChange = (checked: boolean) => {
    onChange({ use_original_text_for_check: checked });
  };

  return (
    <>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={isSettingsEnabled(check.state)} size="small" onChange={onEnabledChange} />
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Guardrails: Step: Enabled: Title')}
          </IbTypography.Paragraph>
        </IbTypography>
      </div>
      <CheckBehaviorField value={check.behavior} onChange={onBehaviorChange} />
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Step: Name: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: Step: Name: Description')}
        </IbTypography.Paragraph>
        <IbInput value={check.name ?? ''} onChange={onNameChange} />
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Step: BlockMessage: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: Step: BlockMessage: Description')}
        </IbTypography.Paragraph>
        <IbInput value={check.block_message} onChange={onBlockMessageChange} />
      </div>
      {showErrorPolicy && (
        <div className={ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Guardrails: ErrorPolicy: Title')}
          </IbTypography.Paragraph>
          <IbSelect options={errorPolicyOptions} value={check.on_error} onChange={onErrorPolicyChange} />
        </div>
      )}
      {showTimeout && (
        <div className={ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Guardrails: Timeout: Title')}
          </IbTypography.Paragraph>
          <IbTypography.Paragraph type="secondary">
            {t('Agent: Guardrails: Timeout: Description')}
          </IbTypography.Paragraph>
          <IbNumberInput min={0} value={check.timeout_seconds ?? undefined} onChange={onTimeoutChange} />
        </div>
      )}
      <div className={ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch
            checked={check.use_original_text_for_check}
            size="small"
            onChange={onUseOriginalTextForCheckChange}
          />
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Guardrails: UseOriginalTextForCheck: Title')}
          </IbTypography.Paragraph>
        </IbTypography>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: UseOriginalTextForCheck: Description')}
        </IbTypography.Paragraph>
      </div>
    </>
  );
};

export default CommonCheckParameters;
